
import { RecommendationService } from "../services/recommendation.service.js";

import { renderIcon } from "../utils/icons.js";
import { locale as LocaleService } from "../services/LocaleService.js";

export const RecommendationPage = async () => {
    const container = document.createElement('div');
    // [FIX] Removed 'overflow-hidden', added 'overflow-y-auto' to enable scrolling
    // Added 'pt-20' to ensure content isn't hidden behind fixed headers if any
    container.className = "min-h-screen w-full relative flex flex-col items-center justify-start p-4 overflow-y-auto scrollbar-hide";

    // 1. Immutable Background (login.webp)
    const bg = document.createElement('div');
    bg.className = "fixed inset-0 z-0"; // Changed to fixed to stay during scroll
    bg.innerHTML = `
        <img src="/login.webp" class="w-full h-full object-cover opacity-90" alt="Background">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
    `;
    container.appendChild(bg);

    // 2. Content Wrapper
    const content = document.createElement('div');
    // Added 'my-auto' to center vertically if content is short, but allow scroll if long
    content.className = "relative z-10 w-full max-w-5xl animate-fade-in flex flex-col items-center text-center my-auto py-10";
    container.appendChild(content);

    // Helper: Render Loading State
    const renderLoading = () => {
        content.innerHTML = `
            <div class="flex flex-col items-center gap-6">
                <div class="w-16 h-16 rounded-full border-4 border-white/20 border-t-fuchsia-400 animate-spin"></div>
                <div class="space-y-2">
                    <h2 class="text-2xl font-bold text-white tracking-wide font-display">${LocaleService.t('recommendation.loading_title')}</h2>
                    <p class="text-white/60 text-sm">${LocaleService.t('recommendation.loading_desc')}</p>
                </div>
            </div>
        `;
    };

    // Helper: Internal Refresh Logic
    const refreshSuggestions = async () => {
        // [FIX] Internal Refresh instead of reload() to prevent Auth Race Condition
        renderLoading();
        // Clear specific cache for variety (optional, handled in service)
        sessionStorage.removeItem('rec_candidate_pool_v3');

        const user = window.user;
        if (user) {
            try {
                const result = await RecommendationService.getEmptyShelfPicks(user.uid);
                renderResults(result, user);
            } catch (e) {
                console.error("Refresh failed", e);
            }
        }
    };

    // Helper: Render Results
    const renderResults = (result, user) => {
        // [HYBRID SUPPORT] Handle both Object and Array(with props) returns
        let picks = [];
        let isFallback = false;

        if (Array.isArray(result)) {
            picks = result;
            isFallback = result.isFallback || false;
        } else if (result && result.items) {
            picks = result.items;
            isFallback = result.isFallback;
        }

        if (!picks || picks.length === 0) {
            // Should rarely happen now due to fallback, but keep as safety
            content.innerHTML = `
                <div class="bg-white/10 backdrop-blur-xl border border-white/20 p-10 rounded-2xl max-w-lg shadow-2xl">
                     <span class="material-symbols-outlined text-5xl text-white/40 mb-4">auto_stories</span>
                     <h2 class="text-2xl font-bold text-white mb-2">${LocaleService.t('recommendation.empty_data_title')}</h2>
                     <button onclick="window.router.navigate('/')" class="mt-4 px-6 py-3 bg-white text-black font-bold rounded-xl hover:scale-105 transition-transform">
                        ${LocaleService.t('recommendation.btn_browse')}
                     </button>
                </div>
            `;
            return;
        }

        const userName = user.displayName || '사용자';

        // [POETIC TEXT] Distinguish between Personalized vs Fallback (Cold Start)
        let title = '';
        let subtitle = '';

        if (isFallback) {
            // Cold Start Message
            title = LocaleService.t('recommendation.fallback_title', { name: userName });
            subtitle = LocaleService.t('recommendation.fallback_desc');
        } else {
            // Personalized Message
            title = LocaleService.t('recommendation.personal_title', { name: userName });
            subtitle = LocaleService.t('recommendation.personal_desc');
        }

        let html = `
            <div class="mb-10 animate-slide-up">
                <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-fuchsia-300 text-sm font-medium mb-4 backdrop-blur-md shadow-lg shadow-fuchsia-500/20">
                    <span class="material-symbols-outlined text-sm">auto_awesome</span> ${LocaleService.t('recommendation.label_empty_shelf')}
                </div>
                <h1 class="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight font-display drop-shadow-lg">
                    ${title}
                </h1>
                <p class="text-white/60 font-light text-lg whitespace-pre-line leading-relaxed">${subtitle}</p>
            </div>

            <!-- Cards Container -->
            <div class="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 w-full px-4 perspective-1000">
        `;

        picks.forEach((series, idx) => {
            // Staggered Animation Delay
            const delay = idx * 100;
            const poster = series.coverImage || series.thumbnail || '/placeholder.png';

            html += `
                <div class="group relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer transform hover:-translate-y-2 transition-all duration-500 shadow-2xl shadow-black/50 hover:shadow-fuchsia-500/30 ring-1 ring-white/10 hover:ring-fuchsia-400/50 animate-fade-in-up"
                     style="animation-delay: ${delay}ms"
                     onclick="window.router.navigate('/series/${series.id}')">
                    
                    <!-- Poster -->
                    <img src="${poster}" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="${series.title}">
                    
                    <!-- Gradient Overlay -->
                    <div class="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80 group-hover:opacity-60 transition-opacity"></div>
                    
                    <!-- Content -->
                    <div class="absolute bottom-0 inset-x-0 p-4 text-left">
                        <div class="text-xs text-fuchsia-300 font-bold mb-1 tracking-wider uppercase opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                             Pick No.${idx + 1}
                        </div>
                        <h3 class="text-white font-bold text-lg leading-snug line-clamp-2 mb-1 group-hover:text-fuchsia-100 transition-colors">
                            ${series.title}
                        </h3>
                        <p class="text-white/60 text-xs line-clamp-1">${series.authorName || 'Unknown'}</p>
                    </div>

                    <!-- Tags (On Hover) -->
                    <div class="absolute top-0 inset-x-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                         <div class="flex flex-wrap gap-1 justify-end">
                            ${(series.tags || []).slice(0, 2).map(t => `<span class="text-[10px] bg-black/50 backdrop-blur text-white px-2 py-1 rounded-full border border-white/10">#${t}</span>`).join('')}
                         </div>
                    </div>
                </div>
            `;
        });

        html += `</div>`; // End Grid

        // Bottom Action
        html += `
            <div class="mt-12 animate-fade-in pb-10" style="animation-delay: 800ms">
                <button id="btn-refresh" class="px-8 py-3 bg-white/5 hover:bg-white/10 border border-white/20 rounded-full text-white/80 font-medium transition-all hover:scale-105 flex items-center gap-2 backdrop-blur-md">
                    <span class="material-symbols-outlined">refresh</span> ${LocaleService.t('recommendation.btn_refresh')}
                </button>
            </div>
        `;

        content.innerHTML = html;

        // Bind Click (since onclick string eval scope is tricky with internal functions)
        setTimeout(() => {
            const btn = document.getElementById('btn-refresh');
            if (btn) btn.onclick = refreshSuggestions;
        }, 0);

        // Add Perspective CSS if not exists
        if (!document.getElementById('rec-css')) {
            const style = document.createElement('style');
            style.id = 'rec-css';
            style.innerHTML = `
                .perspective-1000 { perspective: 1000px; }
                .font-display { font-family: 'Outfit', sans-serif; }
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `;
            document.head.appendChild(style);
        }
    };


    // 3. Execution Logic
    renderLoading();

    try {
        const user = window.user;
        if (!user) {
            window.router.navigate('/login');
            return container;
        }

        // Wait a bit for dramatic effect (and ensure service load)
        setTimeout(async () => {
            try {
                // Expecting object { items, isFallback }
                // Check if Service is available
                if (!RecommendationService || !RecommendationService.getEmptyShelfPicks) {
                    throw new Error("Service not initialized");
                }

                const result = await RecommendationService.getEmptyShelfPicks(user.uid);
                renderResults(result, user);

                // [NEW] Sync Taste Profile to Server (Fire & Forget)
                // Allows Admin Strategy Dashboard to target similar users
                RecommendationService.syncUserTaste(user.uid).catch(err => console.error("Taste Sync Error", err));
            } catch (innerError) {
                console.error("Rec Page Async Error:", innerError);
                content.innerHTML = `
                    <div class="bg-red-500/10 backdrop-blur-md border border-red-500/20 p-8 rounded-2xl max-w-lg text-center animate-fade-in">
                        <span class="material-symbols-outlined text-4xl text-red-400 mb-4">error_outline</span>
                        <h3 class="text-xl font-bold text-white mb-2">${LocaleService.t('recommendation.error_title')}</h3>
                        <p class="text-white/60 mb-6 text-sm">${innerError.message || '데이터를 불러오는 중 문제가 발생했습니다.'}</p>
                        <button onclick="window.location.reload()" class="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold transition-colors">
                            ${LocaleService.t('recommendation.btn_retry')}
                        </button>
                    </div>
                `;
            }
        }, 1500);

    } catch (e) {
        console.error("Recommendation Page Error:", e);
        content.innerHTML = `<div class="text-red-400">시스템 오류가 발생했습니다.<br>${e.message}</div>`;
    }

    return container;
};
